import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import ActivityIndicator from 'client/modules/App/components/ActivityIndicator/ActivityIndicator';
import {
  useApproveConsentMutation,
  useDenyConsentMutation,
  useGetConsentInfoQuery,
} from 'client/modules/Settings/mcpOAuthSlice';
import LayoutFocused from 'client/modules/App/components/LayoutFocused/LayoutFocused';
import React, { useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

const messages = defineMessages({
  title: {
    id: 'OAuthConsentPage.title',
    defaultMessage: 'Connect AI client',
  },
  description: {
    id: 'OAuthConsentPage.description',
    defaultMessage: '{clientName} is requesting access to your Promoter Ninja data.',
  },
  scopesTitle: {
    id: 'OAuthConsentPage.scopesTitle',
    defaultMessage: 'Requested permissions',
  },
  orgLabel: {
    id: 'OAuthConsentPage.orgLabel',
    defaultMessage: 'Organization',
  },
  approve: {
    id: 'OAuthConsentPage.approve',
    defaultMessage: 'Allow access',
  },
  deny: {
    id: 'OAuthConsentPage.deny',
    defaultMessage: 'Deny',
  },
  noApiAccess: {
    id: 'OAuthConsentPage.noApiAccess',
    defaultMessage:
      'The selected organization does not include API/MCP access. Upgrade to Growth or higher.',
  },
  invalidRequest: {
    id: 'OAuthConsentPage.invalidRequest',
    defaultMessage: 'This authorization request is invalid or has expired.',
  },
});

const SCOPE_LABELS = {
  'surveys:read': {
    id: 'OAuthConsentPage.scope.surveysRead',
    defaultMessage: 'Read survey metadata',
  },
  'responses:read': {
    id: 'OAuthConsentPage.scope.responsesRead',
    defaultMessage: 'Read responses, NPS trends, and tags',
  },
  'saved_searches:read': {
    id: 'OAuthConsentPage.scope.savedSearchesRead',
    defaultMessage: 'Read saved search filters',
  },
  'links:read': {
    id: 'OAuthConsentPage.scope.linksRead',
    defaultMessage: 'Read survey share links',
  },
  'people:read': {
    id: 'OAuthConsentPage.scope.peopleRead',
    defaultMessage: 'Read people and delivery state',
  },
  'activity:read': {
    id: 'OAuthConsentPage.scope.activityRead',
    defaultMessage: 'Read delivery funnel events (shown, sent, scheduled)',
  },
};

function OAuthConsentPage() {
  const { formatMessage } = useIntl();
  const [searchParams] = useSearchParams();
  const authorizationId = searchParams.get('authorization_id');
  const { data, isLoading, isError } = useGetConsentInfoQuery(authorizationId, {
    skip: !authorizationId,
  });
  const [approveConsent, { isLoading: isApproving }] = useApproveConsentMutation();
  const [denyConsent, { isLoading: isDenying }] = useDenyConsentMutation();
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [error, setError] = useState(null);

  const orgsWithAccess = data?.organizations?.filter(o => o.hasApiAccess) || [];
  const effectiveOrgId = selectedOrgId || orgsWithAccess[0]?.id || '';

  const handleApprove = async () => {
    setError(null);
    try {
      const result = await approveConsent({
        authorization_id: authorizationId,
        org_id: effectiveOrgId,
      }).unwrap();
      window.location.href = result.redirectUrl;
    } catch (err) {
      setError(err?.data?.message || err?.message);
    }
  };

  const handleDeny = async () => {
    setError(null);
    try {
      const result = await denyConsent({ authorization_id: authorizationId }).unwrap();
      window.location.href = result.redirectUrl;
    } catch (err) {
      setError(err?.data?.message || err?.message);
    }
  };

  return (
    <LayoutFocused title={formatMessage(messages.title)} maxWidth="sm">
      {isLoading && <ActivityIndicator inline sx={{ py: 4 }} />}
      {(isError || !authorizationId) && !isLoading && (
        <Alert severity="error">{formatMessage(messages.invalidRequest)}</Alert>
      )}
      {data && (
        <Box>
          <Typography sx={{ mb: 2 }}>
            <FormattedMessage
              {...messages.description}
              values={{ clientName: <strong>{data.clientName}</strong> }}
            />
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {formatMessage(messages.scopesTitle)}
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 3 }}>
            {data.scopes.map(scope => (
              <li key={scope}>
                <FormattedMessage {...(SCOPE_LABELS[scope] || { defaultMessage: scope })} />
              </li>
            ))}
          </Box>
          {orgsWithAccess.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {formatMessage(messages.noApiAccess)}
            </Alert>
          ) : (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{formatMessage(messages.orgLabel)}</InputLabel>
              <Select
                value={effectiveOrgId}
                label={formatMessage(messages.orgLabel)}
                onChange={e => setSelectedOrgId(e.target.value)}
              >
                {orgsWithAccess.map(org => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleApprove}
              disabled={!effectiveOrgId || isApproving || isDenying}
            >
              {formatMessage(messages.approve)}
            </Button>
            <Button variant="outlined" onClick={handleDeny} disabled={isApproving || isDenying}>
              {formatMessage(messages.deny)}
            </Button>
          </Box>
        </Box>
      )}
    </LayoutFocused>
  );
}

export default OAuthConsentPage;
